'use client'

import { useState, type CSSProperties } from 'react'
import EventGallery from '@/components/EventGallery'
import { normalizeGallery, type EventGalleryShape } from '@/lib/galleryShape'
import { normalizeSiteSlider, type EventSiteSliderShape } from '@/lib/siteSliderShape'

/**
 * Une soirée passée présentée en BANDEAU PLEINE LARGEUR (DA « mur des soirées »,
 * portée de LDC et recolorée aux tokens GDE : nuit #021734 + accent #C2A66B) :
 * photo de couverture, nom, date / lieu, et un bouton qui ouvre la galerie de
 * CETTE soirée dans le MODAL plein écran (chargée à la demande au 1er clic).
 *
 * La photo vient de l'affiche/galerie de la soirée (réglée au CRM). Sans photo,
 * un fond sobre nuit/or prend le relais. Si `boutonHref` pointe vers une page,
 * le bandeau devient un lien vers cette page au lieu d'ouvrir le modal.
 */

const ADMIN_API = (process.env.NEXT_PUBLIC_ADMIN_API_URL || 'https://admin.example.com').replace(/\/$/, '')

export interface BandeauSouvenirProps {
  slug: string
  titre: string
  date?: string
  location?: string
  image?: string
  pos?: string
  boutonLabel?: string
  /** Cible du bouton. Vide ou 'galerie' → ouvre le modal de la galerie de
      cette soirée ; sinon une adresse (page) → le bandeau est un lien. */
  boutonHref?: string
}

export default function BandeauSouvenir({ slug, titre, date, location, image, pos, boutonLabel, boutonHref }: BandeauSouvenirProps) {
  const [ouvert, setOuvert] = useState(false)
  const [chargement, setChargement] = useState(false)
  const [erreur, setErreur] = useState(false)
  const [galerie, setGalerie] = useState<EventGalleryShape | null>(null)
  const [slider, setSlider] = useState<EventSiteSliderShape>([])

  /* Ouvre la galerie de CETTE soirée dans le MODAL plein écran (EventGallery
     contrôlé). Chargée à la demande au premier clic (parfois 400+ photos). */
  const ouvrir = async () => {
    if (galerie) { setOuvert(true); return }
    if (chargement) return
    setChargement(true)
    setErreur(false)
    try {
      const [rg, rs] = await Promise.all([
        fetch(`${ADMIN_API}/api/public/event/${slug}/gallery`),
        fetch(`${ADMIN_API}/api/public/event/${slug}/site-slider`),
      ])
      const dg = rg.ok ? await rg.json() : null
      const ds = rs.ok ? await rs.json() : null
      setGalerie(normalizeGallery(dg?.event?.gallery))
      setSlider(normalizeSiteSlider(ds?.event?.siteSliderImages))
      setOuvert(true)
    } catch {
      setErreur(true)
    } finally {
      setChargement(false)
    }
  }

  const sousTitre = [date, location].filter(Boolean).join(' · ')
  const cible = boutonHref?.trim() ?? ''
  const ouvreModal = cible === '' || cible === 'galerie'
  const label = chargement ? 'Chargement…' : `${boutonLabel?.trim() || 'Revivez la soirée'} ›`

  const classeOuter = 'w-full relative block text-center overflow-hidden'
  const styleOuter: CSSProperties = {
    border: 'none',
    borderTop: '1px solid color-mix(in srgb, var(--color-brand-accent) 28%, transparent)',
    cursor: 'pointer',
    minHeight: 'clamp(320px, 56vh, 560px)',
    padding: 'clamp(48px, 8vw, 96px) 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--color-brand-nuit, #021734)',
    textDecoration: 'none',
  }

  const contenu = (
    <>
      {image ? (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={image}
            alt=""
            loading="lazy"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: pos || 'center' }}
          />
          <span aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(2,23,52,0.55), rgba(2,23,52,0.82))' }} />
        </>
      ) : (
        <span aria-hidden style={{ position: 'absolute', inset: 0, background: 'radial-gradient(120% 90% at 50% 10%, rgba(194,166,107,0.16), transparent 60%), linear-gradient(160deg,#0a2448,#021734 70%)' }} />
      )}

      <span style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {sousTitre && (
          <span
            className="uppercase font-medium"
            style={{ fontFamily: 'var(--font-body, inherit)', fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)', letterSpacing: '0.34em', color: 'color-mix(in srgb, var(--color-brand-accent) 90%, white)' }}
          >
            {sousTitre}
          </span>
        )}
        <span
          className="italic"
          style={{
            marginTop: 14,
            fontFamily: 'var(--font-heading, inherit)',
            fontWeight: 400,
            fontSize: 'clamp(2rem, 1.2rem + 3.2vw, 4rem)',
            lineHeight: 1.05,
            letterSpacing: '-0.01em',
            color: 'var(--color-brand-ivory, #FBF6EC)',
            textShadow: '0 2px 24px rgba(0,0,0,0.5)',
          }}
        >
          {titre}
        </span>
        <span aria-hidden style={{ display: 'block', width: 36, height: 1, background: 'color-mix(in srgb, var(--color-brand-accent) 70%, transparent)', margin: '22px auto' }} />
        <span
          className="uppercase"
          style={{
            display: 'inline-block',
            fontFamily: 'var(--font-body, inherit)',
            fontSize: 'clamp(11px, 0.7rem + 0.1vw, 13px)',
            letterSpacing: '0.16em',
            color: 'var(--color-brand-ink, #021734)',
            background: 'var(--color-brand-accent, #C2A66B)',
            border: '1px solid var(--color-brand-accent, #C2A66B)',
            padding: '12px 26px',
            opacity: chargement ? 0.75 : 1,
          }}
        >
          {label}
        </span>
      </span>
    </>
  )

  return (
    <section style={{ background: 'var(--color-brand-nuit, #021734)' }}>
      {ouvreModal ? (
        <button
          type="button"
          onClick={ouvrir}
          aria-haspopup="dialog"
          disabled={chargement}
          className={classeOuter}
          style={styleOuter}
        >
          {contenu}
        </button>
      ) : (
        <a href={cible} className={classeOuter} style={styleOuter}>
          {contenu}
        </a>
      )}

      {/* Le modal plein écran de la galerie (rendu en portail par EventGallery).
          Monté dès que la galerie est chargée ; sa visibilité suit `ouvert`. */}
      {ouvreModal && galerie && (
        <EventGallery
          gallery={galerie}
          highlights={slider}
          open={ouvert}
          onRequestClose={() => setOuvert(false)}
        />
      )}
      {ouvreModal && erreur && (
        <p className="text-center" style={{ padding: '20px 16px 32px', fontSize: 13, color: 'color-mix(in srgb, var(--color-brand-ivory) 60%, transparent)' }}>
          Les images de cette soirée n&apos;ont pas pu être chargées.
        </p>
      )}
    </section>
  )
}
